import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
} from '@reduxjs/toolkit'
import { setRead } from 'shared/api/socket.api'
import { IBellNotificationPayload, ISocketMessage } from 'shared/models/Socket'
import { dynamicSort } from 'shared/utils/array-helpers'
import { getNotifications } from './selectors'
import type { RootState } from '../store'

export const notificationsMessages = createEntityAdapter<
  ISocketMessage<IBellNotificationPayload>
>({
  sortComparer: dynamicSort<ISocketMessage<IBellNotificationPayload>>(
    'ReceivedDateTime',
    'desc'
  ),
})

const notificationsSlice = createSlice({
  name: 'notifications',
  initialState: notificationsMessages.getInitialState(),

  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(markAllNotificationsAsRead.fulfilled, (state) => {
      notificationsMessages.removeAll(state)
    })
    builder.addCase(markNotificationAsRead.fulfilled, (state, action) => {
      notificationsMessages.removeOne(state, action.payload)
    })
    builder.addCase(addNotification.fulfilled, (state, { payload }) => {
      if (payload) {
        notificationsMessages.addOne(state, payload)
      }
    })
  },
})

// THUNKS

export const addNotification = createAsyncThunk(
  'notifications/addNotification',
  async (
    payload: ISocketMessage<IBellNotificationPayload>,
    { dispatch, getState, abort }
  ) => {
    const state = getState() as RootState
    const notifications = getNotifications(state)

    // If we already have the extact same message, do nothing
    const isDuplicateMessage = notifications.some(
      (notification) => notification.id === payload.id
    )
    if (isDuplicateMessage) {
      return abort()
    }

    // If we already have a message for the same item, mark it as read
    const isAggregateMessage = notifications.some(
      ({ Notification }) =>
        Notification.Payload.ItemId === payload.Notification.Payload.ItemId &&
        Notification.Type === payload.NotificationType
    )

    if (isAggregateMessage) {
      dispatch(markNotificationAsRead(payload.id))
      return abort()
    }

    return payload
  }
)

export const markNotificationAsRead = createAsyncThunk(
  'notifications/markNotificationAsRead',
  (id: string) => {
    setRead(id)
    return id
  }
)

export const markAllNotificationsAsRead = createAsyncThunk(
  'notifications/markAllNotificationsAsRead',
  () => {
    // TODO: call endpoint
    return
  }
)

export const { reducer, actions } = notificationsSlice
